import type { ClerkProvider } from '@clerk/react-router';
import type { ComponentProps } from 'react';
import { authDebug } from './authDebug';

type ClerkProviderProps = ComponentProps<typeof ClerkProvider>;

/** The ClerkProvider props that hand clerk-js and Clerk UI to the SDK as first-party code. */
export type BundledClerkOptions = Partial<Pick<ClerkProviderProps, 'Clerk' | 'ui'>>;

/**
 * [MCV-1789] Whether clerk-js and Clerk UI should ship in OUR bundle instead of
 * being loaded from Clerk's CDN. True only for development instances (`pk_test_`).
 *
 * A `pk_test_` key makes @clerk/react load both scripts from `*.clerk.accounts.dev`,
 * a third-party tracking-listed domain. Safari's Advanced Tracking and Fingerprinting
 * Protection (on by default in Private Browsing) hides the URL query string from
 * scripts on such domains, so clerk-js cannot read the satellite hand-off markers
 * (`__clerk_db_jwt`, `__clerk_synced`, `__clerk_satellite_url`) and HLX never
 * receives the session. Bundling makes clerk-js first-party code that reads its own
 * page's URL.
 *
 * Production (`pk_live_`) is deliberately untouched: it already loads clerk-js from
 * our own `clerk.<apex>` FAPI host, which is first-party, and keeps its CDN loading.
 *
 * The bundled versions are pinned on purpose (exact `@clerk/clerk-js` / `@clerk/ui`
 * in package.json), and since [MCV-1790] both environments run those installed
 * versions: bundled on `pk_test_`, and requested by exact version from Clerk's CDN
 * on `pk_live_` (see clerkCdnVersionPins). Bump every `@clerk/*` package together
 * in one ticketed PR ([MCV-1878]: we do not use Dependabot), so staging previews
 * exactly what prod will load and the drift stays a decision.
 */
export const shouldBundleClerk = (publishableKey: string): boolean =>
  publishableKey.startsWith('pk_test_');

/**
 * [MCV-1790] The npm versions the SDK requests clerk-js and Clerk UI at when it loads
 * them from Clerk's CDN. Declared here rather than picked from `ClerkProviderProps`
 * because Clerk's consumer-facing provider types omit these two internal props
 * (`@clerk/shared`'s `InternalClerkScriptProps`), although `@clerk/react-router`'s
 * ClerkProvider forwards them: it spreads its rest props last onto the underlying
 * provider, and `@clerk/shared`'s `loadClerkJsScript` builds the script URL from them.
 */
export type ClerkCdnVersionPins = {
  __internal_clerkJSVersion?: string;
  __internal_clerkUIVersion?: string;
};

/**
 * [MCV-1790] The ClerkProvider props that pin the CDN-loaded clerk-js and Clerk UI to
 * the versions installed here (`VITE_CLERK_JS_VERSION` / `VITE_CLERK_UI_VERSION`,
 * defined by vite.config.ts from node_modules). Without them @clerk/react requests the
 * floating major (`@clerk/clerk-js@6`), so prod could run a different clerk-js than the
 * one staging bundles; with them prod's CDN scripts request exactly the versions staging
 * bundles, so staging previews prod's Clerk. Bump the pins as one group.
 *
 * A key is present only when its version is a non-empty string; with neither (an
 * unreadable install, see vite.config.ts) this is `{}` and the SDK keeps its floating
 * default, as before.
 */
export const clerkCdnVersionPins = (): ClerkCdnVersionPins => {
  const clerkJsVersion: unknown = import.meta.env.VITE_CLERK_JS_VERSION;
  const clerkUiVersion: unknown = import.meta.env.VITE_CLERK_UI_VERSION;
  return {
    ...(typeof clerkJsVersion === 'string' && clerkJsVersion
      ? { __internal_clerkJSVersion: clerkJsVersion }
      : {}),
    ...(typeof clerkUiVersion === 'string' && clerkUiVersion
      ? { __internal_clerkUIVersion: clerkUiVersion }
      : {}),
  };
};

/**
 * [MCV-1789] How long the bundled clerk-js / Clerk UI imports may take before we give
 * up and fall back to the CDN. Matches `@clerk/shared`'s default `scriptLoadTimeout`
 * (15e3, in `loadClerkJsScript.mjs`) for the CDN scripts this bundle replaces, so a
 * stalled chunk is bounded exactly as a stalled script tag was.
 */
export const BUNDLED_CLERK_LOAD_TIMEOUT_MS = 15_000;

/**
 * [MCV-1789] Resolves the `Clerk` and `ui` ClerkProvider options for this
 * publishable key: the bundled clerk-js / Clerk UI on a development instance, `{}`
 * everywhere else (the SDK then keeps its normal CDN script loading).
 *
 * The imports are dynamic so a production visitor never downloads the ~1.5 MB
 * bundles. A failed or stalled import (see BUNDLED_CLERK_LOAD_TIMEOUT_MS) resolves to
 * `{}` rather than rejecting, so the app still boots and falls back to the CDN
 * scripts, which were the behaviour before this change.
 */
export const loadBundledClerkOptions = async (
  publishableKey: string
): Promise<BundledClerkOptions> => {
  if (!shouldBundleClerk(publishableKey)) return {};

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(
      () =>
        reject(
          new Error(`Timed out after ${BUNDLED_CLERK_LOAD_TIMEOUT_MS} ms loading the bundled Clerk`)
        ),
      BUNDLED_CLERK_LOAD_TIMEOUT_MS
    );
  });

  try {
    const [{ Clerk }, { ui }] = await Promise.race([
      Promise.all([import('@clerk/clerk-js'), import('@clerk/ui')]),
      timeout,
    ]);
    // @clerk/clerk-js and @clerk/ui are built against a newer @clerk/shared than
    // the one @clerk/react types its props with, so the same runtime objects do
    // not type-check; cast each at this single seam.
    // `clerk.source` records the OUTCOME; main.tsx's bootstrap event only knows
    // the bundle was requested.
    authDebug('clerk.source', { clerkSource: 'bundled' });
    return {
      Clerk: Clerk as unknown as ClerkProviderProps['Clerk'],
      ui: ui as ClerkProviderProps['ui'],
    };
  } catch (error) {
    authDebug('clerk.bundle_load_failed', {
      message: error instanceof Error ? error.message : String(error),
    });
    console.error('[MCV-1789] Could not load the bundled Clerk; falling back to the CDN.', error);
    authDebug('clerk.source', { clerkSource: 'cdn-fallback' });
    return {};
  } finally {
    // Whichever side settled first, the timer must not outlive the call.
    clearTimeout(timer);
  }
};
